/**
 * Zeitangaben für die Oberfläche.
 *
 * Zwei Arten von Zeit treffen hier aufeinander, und sie dürfen nicht
 * verwechselt werden:
 *
 * **Ortszeit** — wann ein Aufenthalt oder Weg *dort* stattfand. Ein Abendessen
 * in Tokio um 19 Uhr gehört als 19 Uhr in die Zeitachse, nicht als 12 Uhr.
 * Diese Zeiten rechnet Encom über `offset_min` aus und liefert sie fertig.
 *
 * **Lokalzeit** — wann etwas im Betrieb geschah: ein Import, eine Auflösung.
 * Dafür zählt die Zeitzone dessen, der hinsieht. Encom liefert ISO-UTC, hier
 * wird daraus die Zeit des Browsers.
 *
 * Ohne diese Umrechnung erschien ein Import um 01:30 MESZ als „23:30 am
 * Vortag" — richtig in UTC, aber niemand lebt dort.
 */

/** Datum in Lokalzeit: 17.08.2026 */
export function datumLokal(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Datum und Uhrzeit in Lokalzeit: 17.08.2026, 14:24 */
export function zeitpunktLokal(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Uhrzeit in Lokalzeit: 17:51 */
export function uhrzeitLokal(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

/** Tag und Uhrzeit in Lokalzeit, ohne Jahr: 16.08. 17:51 */
export function tagUndUhrzeitLokal(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}. ${uhrzeitLokal(iso)}`;
}

/**
 * Uhrzeit vor Ort — aber nur, wenn sie von der örtlichen abweicht.
 *
 * Gibt `null` zurück, solange beide Zeitzonen übereinstimmen. So steht bei
 * einem Tag in Köln nichts Überflüssiges, während in Tokio sichtbar wird, dass
 * das Abendessen dort um 19 Uhr stattfand und nicht um 12.
 *
 * Fehlt der Ortsversatz ganz (Rohspuren tragen keinen), gilt die Zeit als
 * unbestimmt und es wird nichts angezeigt — eine Null wäre hier eine Behauptung.
 */
export function ortszeitFallsAbweichend(
  iso: string | null | undefined,
  offsetMin: number | null | undefined,
): string | null {
  if (!iso || offsetMin === null || offsetMin === undefined) return null;

  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;

  // getTimezoneOffset zählt andersherum: westlich von Greenwich positiv.
  const hierOffsetMin = -d.getTimezoneOffset();
  if (hierOffsetMin === offsetMin) return null;

  const vorOrt = new Date(d.getTime() + offsetMin * 60_000);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(vorOrt.getUTCHours())}:${p(vorOrt.getUTCMinutes())}`;
}

/** Der Ortsversatz als Text für einen Hinweis: „UTC+9“. */
export function versatzText(offsetMin: number | null | undefined): string {
  if (offsetMin === null || offsetMin === undefined) return "";
  const zeichen = offsetMin < 0 ? "−" : "+";
  const betrag = Math.abs(offsetMin);
  const stunden = Math.floor(betrag / 60);
  const minuten = betrag % 60;
  return `UTC${zeichen}${stunden}${minuten ? `:${String(minuten).padStart(2, "0")}` : ""}`;
}

/** Jahreszahl in Lokalzeit. */
export function jahrLokal(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return String(d.getFullYear());
}

/**
 * Heutiges Datum als JJJJ-MM-TT in Lokalzeit.
 *
 * `toISOString().slice(0, 10)` gäbe das UTC-Datum — nach Mitternacht MESZ noch
 * das von gestern. Die Datumsfelder der Zeitachse arbeiten aber mit dem Tag,
 * den der Benutzer meint.
 */
export function heuteLokal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
